import { consumeRouteFocusReset, requestRouteFocusReset } from "../lib/deferred-heading-focus";
import { consultNavigationGuard, consultNavigationGuardOnPopState } from "./navigation-guard";

/**
 * The single source of truth for the SPA's current location.
 *
 * A module-level store (mirroring the {@link ./navigation-guard} singleton)
 * subscribed via `useSyncExternalStore`, so every `usePortalPath` caller and
 * every `RouterLink` reads and writes the *same* location. Consolidating here
 * is what lets Back/Forward interception be a single decision: one popstate
 * listener updates one store and every subscriber re-renders — no rebroadcasting
 * a synthetic popstate to resync sibling hook instances, and no guard-muting
 * dance to keep that rebroadcast from re-prompting.
 *
 * Only pushes (`navigate`) consult the unsaved-work guard; `replace` stays
 * unguarded so system redirects (legacy path rewrites) are never trapped behind
 * a dialog.
 */

export interface Location {
  pathname: string;
  search: string;
}

function readLocation(): Location {
  return {
    pathname: globalThis.location?.pathname ?? "/",
    search: globalThis.location?.search ?? "",
  };
}

// `useSyncExternalStore` compares snapshots by identity, so a cached object is
// handed out until the location actually changes. Re-reading `location` on
// every call would mint a fresh object each render and loop React forever.
let snapshot: Location = readLocation();

const listeners = new Set<() => void>();
let currentHistoryEntryIsFreshPush = false;

function emit(): void {
  for (const listener of listeners) listener();
}

/** Publishes a location or history-entry change and wakes every subscriber. */
function setLocation(next: Location, freshPush = false, notifySameLocation = false): void {
  currentHistoryEntryIsFreshPush = freshPush;
  if (
    !notifySameLocation &&
    next.pathname === snapshot.pathname &&
    next.search === snapshot.search
  ) {
    return;
  }
  snapshot = next;
  emit();
}

/**
 * Every entry this store creates carries a monotonic index in its history
 * state. A guarded Back/Forward can then be rewound and re-applied with
 * `history.go(±delta)` — real traversals over the entries the browser already
 * has — instead of minting replacement `pushState` entries, which corrupts the
 * chain (after a confirmed Back the next Back would return to the page just
 * left).
 */
const HISTORY_IDX_KEY = "__hubHistoryIdx";

/** Index of the entry currently on screen; adopted from history.state. */
let currentIdx = 0;
let idxInitialized = false;

/**
 * A traversal this store issued itself and must intercept when its popstate
 * lands: `rewind` swallows the guard's rewind (optionally chaining a replay
 * the user confirmed before the rewind settled); `replay` applies the
 * confirmed destination without re-consulting the guard.
 */
let pendingTraversal:
  | { kind: "rewind"; onSettled?: () => void }
  | { kind: "replay"; idx: number | null }
  | null = null;

function readEntryIdx(state: unknown): number | null {
  if (!state || typeof state !== "object") return null;
  const idx = (state as Record<string, unknown>)[HISTORY_IDX_KEY];
  return typeof idx === "number" ? idx : null;
}

/** Adopt (or stamp) the index of the entry the SPA booted on. */
function initEntryIdx(): void {
  if (idxInitialized) return;
  idxInitialized = true;
  const existing = readEntryIdx(globalThis.history?.state);
  if (existing !== null) {
    currentIdx = existing;
    return;
  }
  currentIdx = 0;
  // Stamp against the LIVE URL, not the cached snapshot — a push issued
  // before the first subscription (or after a test swapped the window) must
  // not rewrite the current entry's address to a stale one. The hash rides
  // along untouched (the store doesn't track it, but replaceState rewrites
  // the address bar), and any state another library parked on the boot
  // entry is merged rather than replaced.
  const live = readLocation();
  const bootState = globalThis.history?.state;
  globalThis.history?.replaceState?.(
    {
      ...(bootState && typeof bootState === "object" ? bootState : {}),
      [HISTORY_IDX_KEY]: 0,
    },
    "",
    `${live.pathname}${live.search}${globalThis.location?.hash ?? ""}`,
  );
}

function onPopState(event: PopStateEvent): void {
  const pending = pendingTraversal;
  if (pending?.kind === "rewind") {
    // Our own rewind landing back on the on-screen entry: the URL matches the
    // snapshot again, nothing renders, and no focus reset is consumed (no
    // navigation happened). A confirm that raced the rewind chains here.
    pendingTraversal = null;
    pending.onSettled?.();
    return;
  }
  if (pending?.kind === "replay") {
    // The confirmed traversal re-applied — commit it without re-prompting.
    pendingTraversal = null;
    consumeRouteFocusReset();
    currentIdx = readEntryIdx(event.state) ?? pending.idx ?? currentIdx;
    setLocation(readLocation());
    return;
  }

  // A traversal owns focus and scroll natively, so a focus reset queued by an
  // earlier push must not fire on Back/Forward. (A push to the *current* path
  // never re-runs the route effect, so its request can still be pending this
  // long after.)
  consumeRouteFocusReset();
  const next = readLocation();
  const previous = snapshot;
  const newIdx = readEntryIdx(event.state);
  // A URL that already matches the store needs no render, but the traversal
  // still moved the history cursor.
  if (next.pathname === previous.pathname && next.search === previous.search) {
    if (newIdx !== null) currentIdx = newIdx;
    setLocation(next, false, true);
    return;
  }
  // The browser has already moved the history entry and the URL by the time
  // this fires, so Back/Forward would silently bypass the unsaved-work guard
  // that in-app navigation consults. Intercepting means: rewind to the entry
  // still on screen, prompt, and only re-apply the traversal once the guard's
  // `proceed` runs. Both legs are real `history.go` traversals over existing
  // entries, so confirming Back from /b to /a leaves a chain whose next Back
  // continues to the entry before /a.
  const delta = newIdx === null ? null : newIdx - currentIdx;
  const restore = () => {
    if (delta !== null) {
      pendingTraversal = { kind: "rewind" };
      globalThis.history?.go?.(-delta);
      return;
    }
    // Entry this store never stamped (predates the SPA): its offset is
    // unknowable, so rewind by pushing a fresh copy of the on-screen page —
    // the duplicated-entry cost applies only to this foreign-entry edge.
    globalThis.history?.pushState?.(
      { [HISTORY_IDX_KEY]: ++currentIdx },
      "",
      `${previous.pathname}${previous.search}`,
    );
  };
  const proceed = () => {
    if (delta !== null) {
      const replay = () => {
        pendingTraversal = { kind: "replay", idx: newIdx };
        globalThis.history?.go?.(delta);
      };
      // A synchronously-confirming guard (window.confirm) can proceed before
      // the rewind's popstate has landed; replaying then would traverse from
      // the wrong cursor, so chain it behind the rewind instead.
      if (pendingTraversal?.kind === "rewind") {
        pendingTraversal = { kind: "rewind", onSettled: replay };
      } else {
        replay();
      }
      return;
    }
    globalThis.history?.pushState?.(
      { [HISTORY_IDX_KEY]: ++currentIdx },
      "",
      `${next.pathname}${next.search}`,
    );
    setLocation(next);
  };
  if (consultNavigationGuardOnPopState(`${next.pathname}${next.search}`, restore, proceed)) {
    return;
  }
  if (newIdx !== null) currentIdx = newIdx;
  setLocation(next);
}

/**
 * Subscribe a `useSyncExternalStore` consumer to location changes.
 *
 * One popstate listener backs the whole app — not one per mounted hook instance
 * — attached lazily on the first subscription and removed when the last
 * subscriber unmounts. Binding at subscribe time (rather than at module load)
 * ties the listener to the `window` that is actually live when the tree mounts,
 * which is what keeps the guard working after a test swaps the jsdom window
 * under this module (a data-URL import Node caches across cases).
 */
export function subscribeLocation(listener: () => void): () => void {
  if (listeners.size === 0) {
    // Re-sync from the live URL on the first subscription. In the app this is a
    // no-op — every location change already flows through this store — but it
    // lets a freshly-mounted tree pick up a URL the store was never told about
    // (an external `history` mutation, or a test's swapped window) instead of
    // rendering a stale first snapshot.
    setLocation(readLocation());
    initEntryIdx();
    globalThis.addEventListener?.("popstate", onPopState);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      globalThis.removeEventListener?.("popstate", onPopState);
      // Mirror the first-subscription re-sync above for the index tracking:
      // the next tree to mount may sit on a different window (a test's
      // swapped jsdom), whose history entries this store never stamped.
      idxInitialized = false;
      pendingTraversal = null;
    }
  };
}

/** The cached current-location snapshot for `useSyncExternalStore`. */
export function getLocationSnapshot(): Location {
  return snapshot;
}

/**
 * History index of the entry currently on screen — the authoritative
 * `__hubHistoryIdx` this store maintains across push/replace/pop. Per-entry
 * scroll restoration keys saved positions on it so Back/Forward lands on the
 * scroll position that entry had.
 */
export function getCurrentHistoryIndex(): number {
  return currentIdx;
}

/**
 * Whether the current entry came from a fresh push rather than a traversal.
 * History indices are positional and can be reused when a push truncates the
 * forward branch, so a saved scroll slot at that index belongs to the discarded
 * entry until the fresh push overwrites it.
 */
export function isCurrentHistoryEntryFreshPush(): boolean {
  return currentHistoryEntryIsFreshPush;
}

/**
 * Push a new SPA entry. An active guard (e.g. an editor with unsaved work) gets
 * first refusal: when it intercepts, it owns re-invoking the commit after the
 * user confirms.
 *
 * `onCommitted` fires when the navigation actually lands — synchronously for
 * an unguarded push, after the user confirms for a guarded one, and never for
 * a cancelled one. Post-navigation side effects (closing a mobile drawer)
 * belong here, not after the call: `pushLocation` returning proves nothing
 * about whether a navigation happened.
 */
export function pushLocation(path: string, onCommitted?: () => void): void {
  const commit = () => {
    initEntryIdx();
    const url = new URL(path, globalThis.location?.origin ?? "http://localhost");
    globalThis.history?.pushState?.(
      { [HISTORY_IDX_KEY]: ++currentIdx },
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
    // Queue the shell's focus handoff for after the route renders. Back/Forward
    // is deliberately excluded because the traversal owns its focus position.
    requestRouteFocusReset();
    setLocation({ pathname: url.pathname, search: url.search }, true, true);
    onCommitted?.();
  };
  if (consultNavigationGuard(path, commit)) return;
  commit();
}

/** Replace the current SPA entry (unguarded — used for system redirects). */
export function replaceLocation(path: string): void {
  initEntryIdx();
  const url = new URL(path, globalThis.location?.origin ?? "http://localhost");
  globalThis.history?.replaceState?.(
    { [HISTORY_IDX_KEY]: currentIdx },
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
  setLocation({ pathname: url.pathname, search: url.search });
}
